import React from 'react';
import { motion } from 'motion/react';

export const Logo = ({ className = "w-8 h-8" }: { className?: string }) => (
  <motion.svg 
    viewBox="15 20 70 60" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg" 
    className={className}
    initial="hidden"
    animate="visible"
    whileHover="hover"
    whileTap="tap"
  >
    <defs>
      {/* Premium Brand Gradient */}
      <linearGradient id="logo-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#0f172a" /> {/* Slate 900 */}
        <stop offset="50%" stopColor="#3f3f46" /> {/* Zinc 700 */}
        <stop offset="100%" stopColor="#71717a" /> {/* Zinc 500 */}
      </linearGradient>

      {/* Dynamic Shimmer */}
      <linearGradient id="shimmer-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="white" stopOpacity="0" />
        <stop offset="50%" stopColor="white" stopOpacity="0.7" />
        <stop offset="100%" stopColor="white" stopOpacity="0" />
        <animateTransform 
          attributeName="gradientTransform" 
          type="translate" 
          from="-1.5 -1.5" 
          to="1.5 1.5" 
          dur="4s" 
          repeatCount="indefinite" 
        />
      </linearGradient>
      
      {/* Studio Aura Filter */}
      <filter id="logo-aura" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="4" result="blur" />
        <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="#6366f1" floodOpacity="0.25" />
        <feComposite in="SourceGraphic" in2="blur" operator="over" />
      </filter>

      {/* Specular Studio Lighting */}
      <filter id="logo-lighting" x="-20%" y="-20%" width="140%" height="140%">
        <feSpecularLighting surfaceScale="10" specularConstant="1.5" specularExponent="40" lightingColor="#ffffff">
          <fePointLight x="30" y="20" z="100" />
        </feSpecularLighting>
        <feComposite in="SourceGraphic" operator="in" />
      </filter>
    </defs>

    <motion.g 
      variants={{
        hover: { 
          scale: 1.1,
          rotate: -2,
          transition: { type: "spring", stiffness: 400, damping: 12 }
        },
        tap: { scale: 0.95 }
      }}
    >
      {/* Main V Structure */}
      <motion.path 
        d="M 55 40 L 45 54 L 37 40 L 27 40 L 45 70 L 69 30 L 57 34 M 69 30 L 72 42" 
        stroke="url(#logo-gradient)" 
        strokeWidth="5.5" 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        fill="none"
        filter="url(#logo-aura)"
        variants={{
          hidden: { pathLength: 0, opacity: 0 },
          visible: { 
            pathLength: 1, 
            opacity: 1, 
            transition: { duration: 2, ease: "circOut" } 
          }
        }}
      />

      {/* The "J" Return Hook and Triangle */}
      <motion.path 
        d="M 73 54 L 73 66 C 73 72, 69 72, 67 72 L 55 72 L 61 62 L 66 69" 
        stroke="url(#logo-gradient)" 
        strokeWidth="5.5" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        fill="none"
        filter="url(#logo-aura)"
        variants={{
          hidden: { pathLength: 0, opacity: 0 },
          visible: { 
            pathLength: 1, 
            opacity: 1, 
            transition: { delay: 1, duration: 1.5, ease: "circOut" } 
          }
        }}
      />

      {/* Core (The "J" Dot) */}
      <motion.circle 
        cx="73" cy="46" r="4" 
        fill="url(#logo-gradient)"
        variants={{
          hidden: { scale: 0, opacity: 0 },
          visible: { 
            scale: 1,
            opacity: 1, 
            transition: { 
              delay: 0.5, 
              duration: 0.5, 
              ease: "circOut"
            } 
          }
        }}
      />

      {/* Shimmer Layer */}
      <motion.path 
        d="M 55 40 L 45 54 L 37 40 L 27 40 L 45 70 L 69 30 L 57 34 M 69 30 L 72 42 M 73 54 L 73 66 C 73 72, 69 72, 67 72 L 55 72 L 61 62 L 66 69" 
        stroke="url(#shimmer-gradient)" 
        strokeWidth="5.5" 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        fill="none"
        opacity="0.5"
      />

      {/* Lighting Highlights */}
      <path 
        d="M 55 40 L 45 54 L 37 40 L 27 40 L 45 70 L 69 30 L 57 34 M 69 30 L 72 42 M 73 54 L 73 66 C 73 72, 69 72, 67 72 L 55 72 L 61 62 L 66 69" 
        stroke="white" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        fill="none"
        opacity="0.2"
        filter="url(#logo-lighting)"
      />
    </motion.g>
  </motion.svg>
);



